/// Game audio via the Web Audio API.
///
/// Same idea as Flutter's SoLoud path: SFX are decoded into memory once, then
/// each play is a fire-and-forget AudioBufferSourceNode. Mixing runs on the
/// browser audio thread.
///
/// Mobile browsers (especially Android Chrome) hitch the rAF loop when SFX
/// start on the same AudioContext as a MediaElementSource BGM, or when
/// createBufferSource/connect/start runs inside the touch handler. Mitigations:
///   - BGM is a plain HTMLAudioElement (not wired into the SFX graph)
///   - SFX buffers are converted to mono at load (less mixer work)
///   - A silent buffer is played on unlock to warm the audio hardware
///   - SFX starts are deferred to a macrotask so jump physics/paint aren't blocked
///
/// Browsers refuse to start audio until a user gesture; `init` only loads,
/// and playback waits for `unlock` (first pointerdown / keydown).

import { allAudioAssets, audioAssets } from './audioAssets';
import { defaultMusicVolume, defaultSfxVolume, musicMaxGain } from './audioConstants';

function createAudioContext(): AudioContext {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  return new Ctx();
}

/** Downmix to mono so each jump SFX is half the mixer work on phones. */
function toMono(ctx: AudioContext, buffer: AudioBuffer): AudioBuffer {
  if (buffer.numberOfChannels === 1) return buffer;

  const mono = ctx.createBuffer(1, buffer.length, buffer.sampleRate);
  const out = mono.getChannelData(0);
  const channelCount = buffer.numberOfChannels;
  const channels: Float32Array[] = [];
  for (let c = 0; c < channelCount; c++) {
    channels.push(buffer.getChannelData(c));
  }
  const inv = 1 / channelCount;
  for (let i = 0; i < buffer.length; i++) {
    let sample = 0;
    for (let c = 0; c < channelCount; c++) {
      sample += channels[c]![i]!;
    }
    out[i] = sample * inv;
  }
  return mono;
}

export class AudioManager {
  musicEnabled = true;
  sfxEnabled = true;
  musicVolume = defaultMusicVolume;
  sfxVolume = defaultSfxVolume;

  private ready = false;
  private unlocked = false;
  /** Set when music was requested before the browser allowed playback. */
  private bgmWanted = false;

  private ctx: AudioContext | null = null;
  private sfxGain: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private silentBuffer: AudioBuffer | null = null;

  /** BGM stays off the Web Audio graph — streaming only. */
  private bgm: HTMLAudioElement | null = null;

  private readonly pendingSfx: string[] = [];
  private sfxFlushScheduled = false;
  private readonly sfxNotifyPort: MessagePort;
  /** Kept so the MessageChannel is not GC'd. */
  private readonly sfxPostPort: MessagePort;

  constructor() {
    // Macrotask that still runs before setTimeout(0) clamps — leaves the
    // touch/key handler so jump physics can paint, then starts SFX ASAP.
    const channel = new MessageChannel();
    this.sfxNotifyPort = channel.port1;
    this.sfxPostPort = channel.port2;
    this.sfxNotifyPort.onmessage = () => this.flushPendingSfx();
  }

  async init(options: {
    musicEnabled: boolean;
    sfxEnabled: boolean;
    musicVolume?: number;
    sfxVolume?: number;
  }): Promise<void> {
    this.musicEnabled = options.musicEnabled;
    this.sfxEnabled = options.sfxEnabled;
    this.musicVolume = options.musicVolume ?? defaultMusicVolume;
    this.sfxVolume = options.sfxVolume ?? defaultSfxVolume;

    try {
      const ctx = createAudioContext();
      this.ctx = ctx;

      const sfxGain = ctx.createGain();
      sfxGain.gain.value = this.sfxEnabled ? this.sfxVolume : 0;
      sfxGain.connect(ctx.destination);
      this.sfxGain = sfxGain;

      this.silentBuffer = ctx.createBuffer(1, 1, ctx.sampleRate);

      const sfxUrls = allAudioAssets.filter((src) => src !== audioAssets.music);
      await Promise.all(
        sfxUrls.map(async (src) => {
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${src}: ${response.status}`);
          }
          const raw = await response.arrayBuffer();
          const decoded = await ctx.decodeAudioData(raw.slice(0));
          this.buffers.set(src, toMono(ctx, decoded));
        }),
      );

      const bgm = new Audio(audioAssets.music);
      bgm.loop = true;
      bgm.preload = 'auto';
      this.applyBgmVolume();
      this.bgm = bgm;

      this.ready = true;
    } catch (error) {
      console.warn('AudioManager.init failed:', error);
    }
  }

  /** Called from the first user gesture, which is what lets audio start. */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;

    const ctx = this.ctx;
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().then(() => this.warmSfxGraph());
    } else {
      this.warmSfxGraph();
    }

    if (this.bgmWanted || this.musicEnabled) {
      this.startBgm();
    }
  }

  /** Prime the audio device so the first jump SFX does not wake hardware mid-game. */
  private warmSfxGraph(): void {
    const ctx = this.ctx;
    const gain = this.sfxGain;
    const silent = this.silentBuffer;
    if (!ctx || !gain || !silent) return;
    try {
      const source = ctx.createBufferSource();
      source.buffer = silent;
      source.connect(gain);
      source.start(0);
    } catch {
      // ignore
    }
  }

  applySettings(options: {
    musicEnabled: boolean;
    sfxEnabled: boolean;
    musicVolume?: number;
    sfxVolume?: number;
  }): void {
    this.musicEnabled = options.musicEnabled;
    this.sfxEnabled = options.sfxEnabled;
    if (options.musicVolume !== undefined) this.musicVolume = options.musicVolume;
    if (options.sfxVolume !== undefined) this.sfxVolume = options.sfxVolume;

    this.applyBgmVolume();
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.sfxEnabled ? this.sfxVolume : 0;
    }

    if (!options.musicEnabled) {
      this.stopBgm();
    } else {
      this.startBgm();
    }
  }

  private applyBgmVolume(): void {
    if (!this.bgm) return;
    this.bgm.volume = this.musicVolume * musicMaxGain;
  }

  startBgm(): void {
    if (!this.ready || !this.musicEnabled || !this.bgm) return;

    if (!this.unlocked) {
      this.bgmWanted = true;
      return;
    }

    try {
      this.applyBgmVolume();
      if (this.bgm.paused) {
        void this.bgm.play().catch((error) => {
          console.warn('AudioManager.startBgm play failed:', error);
        });
      }
      this.bgmWanted = false;
    } catch (error) {
      console.warn('AudioManager.startBgm failed:', error);
    }
  }

  pauseBgm(): void {
    if (!this.ready || !this.bgm) return;
    try {
      this.bgm.pause();
    } catch (error) {
      console.warn('AudioManager.pauseBgm failed:', error);
    }
  }

  resumeBgm(): void {
    if (!this.ready || !this.musicEnabled) return;
    this.startBgm();
  }

  stopBgm(): void {
    if (!this.ready || !this.bgm) return;
    try {
      this.bgm.pause();
      this.bgm.currentTime = 0;
    } catch (error) {
      console.warn('AudioManager.stopBgm failed:', error);
    } finally {
      this.bgmWanted = false;
    }
  }

  playSfx(src: string): void {
    if (!this.ready || !this.sfxEnabled || !this.unlocked) return;
    this.pendingSfx.push(src);
    if (this.sfxFlushScheduled) return;
    this.sfxFlushScheduled = true;
    this.sfxPostPort.postMessage(null);
  }

  private flushPendingSfx(): void {
    this.sfxFlushScheduled = false;
    if (this.pendingSfx.length === 0) return;
    const batch = this.pendingSfx.splice(0, this.pendingSfx.length);
    for (const src of batch) {
      this.playSfxNow(src);
    }
  }

  private playSfxNow(src: string): void {
    const ctx = this.ctx;
    const gain = this.sfxGain;
    const buffer = this.buffers.get(src);
    if (!ctx || !gain || !buffer) return;

    try {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      source.start(ctx.currentTime);
    } catch (error) {
      console.warn(`AudioManager.playSfx(${src}) failed:`, error);
    }
  }

  playJump(): void {
    this.playSfx(audioAssets.jump);
  }

  playDie(): void {
    this.playSfx(audioAssets.die);
  }

  playPoint(): void {
    this.playSfx(audioAssets.point);
  }

  dispose(): void {
    try {
      this.bgm?.pause();
    } catch {
      // ignore
    }
    this.pendingSfx.length = 0;
    this.sfxFlushScheduled = false;
    this.bgm = null;
    this.sfxGain = null;
    this.silentBuffer = null;
    this.buffers.clear();
    const ctx = this.ctx;
    this.ctx = null;
    this.ready = false;
    if (ctx) {
      void ctx.close().catch(() => undefined);
    }
  }
}
